import { EmptyChatGuide } from './EmptyChatGuide'
import { ReplyQuote, ThreadBar, replyToOf, snippet, threadMessages } from './Thread'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { usePing } from '../agents/pingContext'
import { useQuery } from '@tanstack/react-query'
import {
  getProjectMembers,
  parseFileCard,
  projectFileBytes,
  type ProjectMessage,
  type ProjectRow,
} from '../transport/api'
import { useIdleStatus } from '../agents/useIdleStatus'
import { fetchSession } from '../auth/googleSession'
import { BusyAgents } from './BusyAgents'
import { useConvBusy } from './useConvBusy'
import { AttachmentCard } from './Attachment'
import { ProjectFiles, collectFiles } from './ProjectFiles'
import { Avatar } from './Avatar'
import { PixelPet } from '../identity/PixelPet'
import { Markdown } from './Markdown'
import { ProjectImage } from './ProjectImage'
import { parseProjectFileNotices } from './projectFileNotice'
import { useProjectMessages } from './useProjectMessages'
import { useReactions, type Reaction } from './useReactions'
import { roomJumpSession, useRoomBookmarks } from './useRoomBookmarks'
import { RoomBookmarkButton } from './RoomBookmark'
import { onJump, takeJump, type Jump } from '../agents/bookmarks/jump'
import { ReactionChips, ReactionPicker, type NameOf } from './Reactions'
import { ProjectRoster } from './ProjectRoster'
import { withBlobUrl } from '../ui/blobUrl'
import type { OpenDoc } from '../files/openDoc'
import { YouTubeCards } from '../ui/YouTube'
import { canPlayInApp, youtubeVideo, type YouTubeVideo } from '../ui/youtubeLink'
import { GroupQuotaBar } from '../groups/GroupQuotaBar'
import { limitRefusal } from '../groups/limitText'
import { openBoardOn } from '../board/boardNav'
import { openRoomPage } from '../page/pages'
import { messageArea } from '../board/area'
import { AreaChip, AreaPicture } from '../board/AreaChip'
import { Icon } from '../ui/Icon'

/**
 * #projPanel — the project (channel) conversation.
 *
 * A view SWAP of the main area, not an overlay: body.proj-open hides #term entirely so
 * the hidden xterm can't steal keystrokes. The composer in #footbar is shared across
 * terminal / DM / project, which is why sending lives in App, not here.
 *
 * Author identity comes straight from the payload: from_ref is {email, device_id} for a
 * human and {email, device_id, agent: <hash>, label} for an agent — so `from_ref.agent`
 * is both the AGENT marker and the colour seed.
 */

function when(ts: number): string {
  return new Date(ts * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Exported for chatFileClick.test — the row is otherwise module-private. */
export function Message({
  m,
  selfEmail,
  selfFace,
  projectId,
  onOpenDoc,
  reactions,
  quote,
  thread,
  bookmark,
}: {
  m: ProjectMessage
  selfEmail: string | null
  /**
   * THE VIEWER'S OWN FACE, LIVE — owner, 2026-09-13: 更新了使用者的 avatar 之後, 並不會
   * 馬上的反應在 project chat 或是 friends chat 裡面.
   *
   * `from_ref.picture` is BAKED INTO EACH ROW by the chat service when the message is
   * read (friendBridge.withSenderIdentity), so every row already on screen keeps the
   * address the picture had when it was fetched — and the service caches a profile for
   * 60s per instance, so even a refetch can hand back the old one. For everyone else
   * that is fine and eventual; for the person who just pressed Save it is the bug they
   * reported. Their own rows are the one case the client can answer authoritatively,
   * so they are drawn from the live session instead of the row.
   */
  selfFace: string | null
  projectId: string
  onOpenDoc: OpenDoc
  /**
   * Its reactions and the doors to them (owner, 2026-10-17: Slack's emoji under a message). Absent
   * where a message has none to offer — a pending send has no seq yet to react to.
   */
  reactions?: {
    list: Reaction[]
    self: string
    nameOf: NameOf
    onToggle: (emoji: string) => void
    onAdd: () => void
    error: string | null
  }
  /** Above the body: what this reply answers, when that message is not on screen (chat/Thread.tsx). */
  quote?: ReactNode
  /** Under it: Reply, the replies' toggle, and the replies themselves (a group's chat; chat/Thread.tsx). */
  thread?: ReactNode
  /**
   * The viewer's own bookmark on it (owner, 2026-11-01: 在 project 裡面也可以加入書籤 … 只有該使用者自己可以看到), at the end
   * of the reactions row. Absent where it cannot be kept: not a project's room, or a send with no seq yet.
   */
  bookmark?: { state: 'kept' | 'pending' | null; onToggle: () => void; error: string | null }
}) {
  const ping = usePing()
  const agentHash = typeof m.from_ref?.agent === 'string' ? m.from_ref.agent : null
  const name =
    m.from_ref?.label ?? (agentHash ? agentHash.slice(0, 8) : (m.from_ref?.email ?? 'unknown'))
  const owner = m.from_ref?.email ?? null
  const isSelf = !agentHash && owner !== null && owner === selfEmail
  const status = typeof m.status === 'string' ? m.status : null

  /**
   * A file message's body is a JSON card, not prose: its `text` is an optional caption
   * and `attachments[]` carries the files. Rendering the raw JSON as markdown would show
   * the reader a blob of braces.
   */
  const card = parseFileCard(m.body)
  const atts = card?.attachments ?? []
  const caption = card ? (card.text ?? '') : (m.body ?? '')
  /** A question about an area of the whiteboard it names (board/area.ts). */
  const area = typeof m.board === 'string' ? messageArea(m.area) : null
  const boardName = typeof m.board_name === 'string' && m.board_name ? m.board_name : 'Whiteboard'

  /*
   * <chat-file:KEY:name> resolution — the contract avg-main fixed (2026-08-19):
   * KEY is the uuid segment of the attachment's s3 key, resolved ONLY against
   * THIS message's own attachments (`file_id` carries the full s3_key on the
   * DynamoDB path). Found → same byte lane as the card's View button; not
   * found → the pill stays the inert pill it renders as, never a dead click.
   */
  const bodyEl = useRef<HTMLDivElement>(null)
  /*
   * Two key shapes, both honored (the contract settled on content_hash after
   * the uuid form was already minted in the wild; the shapes are disjoint —
   * a content hash is bare hex, a uuid segment carries dashes — so matching
   * both costs nothing and strands no one's old messages):
   *   content_hash === KEY          (sha256[:16], the settled contract)
   *   s3_key uuid segment === KEY   (file_id ends with '/KEY')
   */
  const attForKey = (key: string) =>
    atts.find(
      (a) => a.content_hash === key || a.file_id === key || a.file_id.endsWith(`/${key}`),
    )
  useEffect(() => {
    // Mark the pills that actually resolve, so only real files LOOK tappable.
    const root = bodyEl.current
    if (!root) return
    for (const pill of root.querySelectorAll<HTMLElement>('.chat-file')) {
      const key = pill.getAttribute('data-file-key') ?? ''
      if (attForKey(key)) {
        pill.classList.add('on')
        pill.setAttribute('role', 'button')
        pill.setAttribute('tabindex', '0')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caption, m.body, atts.length])
  /*
   * A YouTube link plays in the app's cover panel — owner, 2026-10-19: 在 Project Chat 跟 Human
   * Chat 裡面也把這個功能做出來 (Clean View had it first; ui/YouTube.tsx). Its picture sits under
   * the message, and the link in the text opens the same player. Where the app may not frame
   * YouTube (an unguarded phone app), both stay links out to YouTube.
   */
  const play = canPlayInApp() ? (v: YouTubeVideo) => onOpenDoc({ url: v.url, name: 'YouTube', mime: '', youtube: v }) : null
  const onBodyClick = (e: React.MouseEvent) => {
    const link = play ? (e.target as HTMLElement).closest?.('a[href]') : null
    const video = link ? youtubeVideo((link as HTMLAnchorElement).href) : null
    if (video && play) {
      e.preventDefault()
      play(video)
      return
    }
    /*
     * A ping chip for MY OWN agent opens that agent's conversation — owner,
     * 2026-09-21: 「如果這個 Agent 是他自己的話,那也可以跳轉過去這個 Agent 他的
     * session」. Only `.ping.mine` carries the coordinates; a friend's chip has
     * none, so it reads as a name and stays put rather than pretending.
     */
    const chip = (e.target as HTMLElement).closest?.('a.ping.mine')
    if (chip) {
      e.preventDefault()
      const session = chip.getAttribute('data-agent-session') ?? ''
      if (session) ping?.open({ session, host: chip.getAttribute('data-agent-host') || null })
      return
    }
    const pill = (e.target as HTMLElement).closest?.('.chat-file')
    if (!pill) return
    const att = attForKey(pill.getAttribute('data-file-key') ?? '')
    if (!att) return
    void projectFileBytes(projectId, att.file_id)
      .then((blob) => {
        withBlobUrl(blob, (url) =>
          onOpenDoc({ url, name: att.name ?? 'file', mime: att.mime ?? '' }),
        )
      })
      .catch(() => {
        /* the card's own View/Download remain the fallback */
      })
  }

  const row = (
    <div className="pmsg" data-seq={bookmark ? m.cursor : undefined}>
      {/* Agents get their pixel-pet headshot; humans get their Google photo (Avatar
          falls back to a coloured initials tile when there is none, or the URL 404s). */}
      {agentHash ? (
        <span className="pmsg-avatar pet">
          {/* Same frame, same clipping, same 30 as Clean View's — see AgentPanel. */}
          <PixelPet name={name} seed={agentHash} size={30} />
        </span>
      ) : (
        <Avatar
          picture={isSelf && selfFace ? selfFace : m.from_ref?.picture}
          label={name}
          email={m.from_ref?.email}
        />
      )}

      <div className="pmsg-main">
        <div className="pmsg-head">
          <span className="pmsg-name">{name}</span>
          {agentHash ? (
            <span className="pmsg-badge agent">agent</span>
          ) : isSelf ? (
            <span className="pmsg-badge you">you</span>
          ) : (
            <span className="pmsg-badge user">user</span>
          )}
          {agentHash && owner && <span className="pmsg-owner">owned by {owner}</span>}
          <span className="pmsg-time">{when(m.ts)}</span>
          {/* Sent from a whiteboard's chat (owner, 2026-10-30): which board — and a way to it. */}
          {typeof m.board === 'string' && area && <AreaChip conv={projectId} board={String(m.board)} boardName={boardName} area={area} text={caption} />}
          {typeof m.board === 'string' && !area && (
            <button
              type="button"
              className="pmsg-board"
              title="Sent from this whiteboard — open it"
              onClick={() => openBoardOn(projectId, String(m.board))}
            >
              <Icon name="dashboard" size={12} />
              <span>{typeof m.board_name === 'string' && m.board_name ? m.board_name : 'Whiteboard'}</span>
            </button>
          )}
          {/* Sent from a page's chat (owner, 2026-11-02): which page — and a way to it, its drawer in this room. */}
          {typeof m.page === 'string' && (
            <button
              type="button"
              className="pmsg-board pmsg-page"
              title="Sent from this page — open it"
              onClick={() => openRoomPage(projectId, String(m.page))}
            >
              <Icon name="description" size={12} />
              <span>{typeof m.page_name === 'string' && m.page_name ? m.page_name : 'Untitled'}</span>
            </button>
          )}
          {status && status !== 'sent' && status !== 'delivered' && (
            <span className={`pmsg-status st-${status}`}>{status}</span>
          )}
        </div>
        {quote}

        {caption ? (
          // click-delegation on a static wrapper is deliberate (fossil eslint-disable removed 2026-09-02)
          <div className="pmsg-body md" ref={bodyEl} onClick={onBodyClick}>
            <Markdown body={caption} {...(ping ? { ping: ping.resolve } : {})} />
            {/*
              * A shared-file NOTICE in a message's prose becomes a preview —
              * owner, 2026-08-14: project chat 裡面遇到檔案標籤可以解析顯示預覽
              * 圖片. When an agent posts a message naming a file it was given
              * (`saved locally at …/project-files/…`), Clean View already draws
              * the picture; this brings the same to the room the file was shared
              * IN. The sentence stays (it carries the size and the fact of the
              * share); the picture goes underneath, and clicking it opens the
              * cover panel, not a tab.
              */}
            {parseProjectFileNotices(caption).map((pf, i) =>
              pf.isImage ? (
                <ProjectImage key={`pfn${i}`} pf={pf} onOpen={onOpenDoc} />
              ) : null,
            )}
            <YouTubeCards text={caption} onPlay={play} />
            {area && <AreaPicture conv={projectId} board={String(m.board)} area={area} text={caption} />}
          </div>
        ) : atts.length === 0 ? (
          <div className="pmsg-body empty">(unavailable)</div>
        ) : null}

        {atts.length > 0 && (
          <div className="pmsg-atts">
            {atts.map((a) => (
              <AttachmentCard
                key={a.file_id}
                att={a}
                projectId={projectId}
                onOpenDoc={onOpenDoc}
              />
            ))}
          </div>
        )}

        {reactions && (
          <ReactionChips
            reactions={reactions.list}
            self={reactions.self}
            nameOf={reactions.nameOf}
            onToggle={reactions.onToggle}
            onAdd={reactions.onAdd}
            extra={bookmark ? <RoomBookmarkButton state={bookmark.state} onToggle={bookmark.onToggle} /> : undefined}
          />
        )}
        {reactions?.error ? <div className="err-line pmsg-error">{reactions.error}</div> : null}
        {bookmark?.error ? <div className="err-line pmsg-error">{bookmark.error}</div> : null}

        {m.error ? <div className="err-line pmsg-error">{refusalText(String(m.error))}</div> : null}
      </div>
    </div>
  )
  if (!thread) return row
  // The thread BESIDE the row (chat.css .pm-thread-under): hovering a reply must not light the message above it.
  return (
    <div className="pm-item">
      {row}
      <div className="pm-thread-under">{thread}</div>
    </div>
  )
}

/** A refused send, in words. */
function refusalText(code: string): string {
  if (code === 'reply_invalid') return 'That message can no longer be replied to.'
  return limitRefusal(code) ?? code
}

export function ProjectPanel({
  project,
  selfEmail,
  serverName,
  onOpenDoc,
  filesOpen,
  onCloseFiles,
  onFileCount,
  onDropFiles,
  rosterOpen,
  onCloseRoster,
  onLeft,
  onJumpAgent,
  onReply,
  locked = false,
  kind = 'project',
}: {
  project: ProjectRow
  /** A friend chat or a Project Chat — the same panel; only the empty chat's greeting differs. */
  kind?: 'friend' | 'project'
  selfEmail: string | null
  /** This edge's display name — the `@ server` suffix for LOCAL agents. Remote
   *  agents carry their host inside the stored label ("session @ host"); local
   *  ones were stored bare, so without this the row can't say where they live. */
  serverName: string
  onOpenDoc: OpenDoc
  filesOpen: boolean
  onCloseFiles: () => void
  /** Reported up so the folder button can show a count badge. */
  onFileCount: (n: number) => void
  /** Files dropped onto the transcript are staged for sharing. */
  onDropFiles: (files: File[]) => void
  /** #projRoster, toggled by tapping the project title in the bar. */
  rosterOpen: boolean
  onCloseRoster: () => void
  /** After a successful leave: the panel's project no longer includes us — the
   *  selection must move off it (staying would poll a chat we can't read). */
  onLeft: () => void
  /** Busy-chip tap: jump straight to that agent's conversation. */
  onJumpAgent: (session: string, host: string | null) => void
  /**
   * Reply under a message — a group's chat (chat/Thread.tsx). The composer is the app's, so the panel only says
   * which thread and what is being answered; the app shows "Replying to …" over the composer and sends with it.
   */
  onReply?: (target: { seq: number; name: string; text: string }) => void
  /** A LOCKED 2-person room — a friend DM rendered as a managed conversation. Hides
   *  "Invite people" (no 3rd human) and "Leave" (dm_has_no_exit); the backend enforces
   *  both regardless, this only removes the dead controls. Agents can still be attached. */
  locked?: boolean
}) {
  const { messages, error, loading, sync, hasOlder, loadingOlder, loadOlder, reach } = useProjectMessages(project.project_id)
  /*
   * BOOKMARKS — owner, 2026-11-01: 我們在 project 裡面也可以加入書籤, 這樣使用者就可以把某一個其他使用者或是 agent 的會話給釘在
   * project 裡面. 這個書籤就只有該使用者自己可以看到. A project's room only: not a group's, not a friend's chat. The list and
   * its corner button are App's (the same query); here, the button under each message and the going-to.
   */
  const bmOn = kind === 'project' && !locked && !project.group_id
  const bm = useRoomBookmarks(bmOn ? project.project_id : null)
  /* The viewer's own face, live. Same query key and fetcher App uses, so this reads
     its cache rather than asking again — and because the profile editor invalidates
     that key on save, a new picture reaches this panel without a reload. */
  const me = useQuery({ queryKey: ['auth-me'], queryFn: fetchSession, staleTime: Infinity })
  const selfFace = me.data?.kind === 'in' ? (me.data.identity.picture ?? null) : null
  const idle = useIdleStatus()
  // The room-scoped busy view: what EVERY member's agents are doing, so the float
  // below no longer goes blind on agents the viewer does not own.
  const convBusy = useConvBusy(project.project_id)

  /*
   * The roster's own queries and mutations moved to ProjectRoster.tsx (M6). The panel
   * keeps this one slim subscription because BusyAgents needs the member list — same
   * key, same fetcher, ONE cache entry, so the two components read one truth (the
   * different-needs-same-key trap from M3 does not apply: both want exactly this).
   */
  const membersQ = useQuery({
    queryKey: ['proj-members', project.project_id],
    queryFn: () => getProjectMembers(project.project_id),
  })
  const members = membersQ.data?.members ?? []

  // Reactions (owner, 2026-10-17): on the messages that have a seq — a pending send has none yet.
  const realSeqs = useMemo(
    () =>
      messages
        .filter((m) => m.status !== 'pending' && m.status !== 'failed' && m.cursor > 0 && m.cursor < Number.MAX_SAFE_INTEGER)
        .map((m) => m.cursor),
    [messages],
  )
  const rx = useReactions(project.project_id, realSeqs)
  const [reactingTo, setReactingTo] = useState<number | null>(null)
  const nameOf: NameOf = (user) => {
    const p = members.find((x) => x.user === user)
    return p?.label ?? p?.email ?? 'Someone who left'
  }

  /*
   * THREADS — a group's chat only (owner, 2026-10-20: group 裡面的留言 … 巢狀的回復, 只有一層; chat/Thread.tsx). The
   * top-level messages in order, each with its replies under it, closed until the reader opens them; replying opens
   * the thread, so what was just sent is seen landing.
   */
  const threaded = !!project.group_id
  const threads = useMemo(() => threadMessages(messages), [messages])
  const [openThreads, setOpenThreads] = useState<Set<number>>(() => new Set())
  /*
   * What the reader has already seen, per room: everything written before they opened it, and every reply in a
   * thread they have opened since. A reply written after that, in a closed thread, is NEW — said on the toggle, since
   * nothing at the bottom of the room shows it. (By the time it was written, not by what had loaded: the history
   * arrives after the first draw, and none of it is news.)
   */
  const openedAt = useRef(Date.now() / 1000)
  const seen = useRef<Set<string>>(new Set())
  const orphans = useRef<Set<string>>(new Set())
  useEffect(() => {
    // Another room: its own threads, closed, and its own "already seen".
    setOpenThreads(new Set())
    openedAt.current = Date.now() / 1000
    seen.current = new Set()
    orphans.current = new Set()
  }, [project.project_id])
  useEffect(() => {
    if (!threaded) return
    const open: number[] = []
    for (const [root, replies] of threads.repliesOf) {
      // A reply that stood on its own (its message older than the history) and has just found its thread as older
      // pages loaded: open that thread, or the reply the reader was looking at would vanish into a closed one.
      // And a reply that failed or is still going: never hidden in a closed thread.
      if (replies.some((r) => orphans.current.has(r.message_id) || r.status === 'failed' || r.status === 'pending')) {
        open.push(root)
      }
    }
    orphans.current = new Set(threads.roots.filter((m) => replyToOf(m) !== null).map((m) => m.message_id))
    if (open.length) {
      setOpenThreads((prev) => (open.every((r) => prev.has(r)) ? prev : new Set([...prev, ...open])))
    }
  }, [threaded, threads])
  const toggleThread = (seq: number) => {
    // Opening a thread grows the room with no scroll of its own: stop following the tail, or the next message would
    // carry the reader past the replies they just opened.
    stick.current = false
    setOpenThreads((prev) => {
      const next = new Set(prev)
      if (next.has(seq)) next.delete(seq)
      else next.add(seq)
      return next
    })
    for (const r of threads.repliesOf.get(seq) ?? []) seen.current.add(r.message_id)
  }
  /** Replies in a thread the reader has not seen: written since the room was opened, not theirs, not yet opened. */
  const unseen = (replies: ProjectMessage[]) =>
    replies.filter((r) => r.ts > openedAt.current && !seen.current.has(r.message_id) && r['mine'] !== true).length
  const messageName = (m: ProjectMessage) => m.from_ref?.label ?? m.from_ref?.email ?? 'someone'
  /** On the server with a seq: something to react to or reply under. Not a send still going, nor one refused (a
   *  refused one keeps the placeholder seq it was drawn with, and a reaction or reply there would name nothing). */
  const settled = (m: ProjectMessage) =>
    m.status !== 'pending' && m.status !== 'failed' && m.cursor > 0 && m.cursor < Number.MAX_SAFE_INTEGER
  /** Reply to `m`, in the thread under `root`: the server is told which message was pressed and puts the reply in that
   *  one's thread (one level), so a thread whose top is gone can still be answered. */
  const reply = (m: ProjectMessage, root: number) => {
    setOpenThreads((prev) => new Set(prev).add(root))
    for (const r of threads.repliesOf.get(root) ?? []) seen.current.add(r.message_id)
    onReply?.({ seq: m.cursor, name: messageName(m), text: snippet(m) })
  }
  /** One message as the transcript draws it, with its reactions; `extra` hangs a quote or a thread on it. */
  const one = (m: ProjectMessage, extra: { quote?: ReactNode; thread?: ReactNode } = {}) => (
    <Message
      key={m.message_id}
      m={m}
      selfEmail={selfEmail}
      selfFace={selfFace}
      projectId={project.project_id}
      onOpenDoc={onOpenDoc}
      {...extra}
      {...(settled(m)
        ? {
            reactions: {
              list: rx.bySeq[m.cursor] ?? [],
              self: rx.self,
              nameOf,
              onToggle: (emoji: string) => rx.toggle(m.cursor, emoji),
              onAdd: () => setReactingTo(m.cursor),
              error: rx.error?.seq === m.cursor ? rx.error.text : null,
            },
          }
        : {})}
      {...(settled(m) && bm.supported
        ? {
            bookmark: {
              state: bm.stateOf(m.cursor),
              onToggle: () => (bm.stateOf(m.cursor) ? bm.letGo(m.cursor) : bm.keep(m)),
              error: bm.writeError?.seq === m.cursor ? bm.writeError.text : null,
            },
          }
        : {})}
    />
  )

  const files = collectFiles(messages, selfEmail)

  useEffect(() => {
    onFileCount(files.length)
  }, [files.length, onFileCount])
  const log = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  const [dropping, setDropping] = useState(false)

  // Follow the tail, but only when the reader is already at the bottom — otherwise a
  // new message yanks them away from what they were reading.
  /*
   * OLDER MESSAGES, ON REACHING THE TOP (owner, 2026-10-02: a project begun at 9:00 showed
   * nothing before 15:16). The page lands ABOVE what the reader is looking at, so the
   * scroll is moved down by exactly the height it added — the line under their eyes stays
   * put. Layout effect, so the jump is never painted.
   */
  const anchor = useRef<{ height: number; top: number } | null>(null)
  const askOlder = () => {
    const el = log.current
    if (!el || !hasOlder || loadingOlder) return
    anchor.current = { height: el.scrollHeight, top: el.scrollTop }
    loadOlder()
  }
  useLayoutEffect(() => {
    const el = log.current
    const a = anchor.current
    if (!el || !a) return
    if (el.scrollHeight !== a.height) {
      el.scrollTop = a.top + (el.scrollHeight - a.height)
      anchor.current = null
    }
  }, [messages])
  useEffect(() => {
    if (!loadingOlder) anchor.current = null
  }, [loadingOlder])

  useEffect(() => {
    const el = log.current
    if (el && stick.current && !anchor.current) el.scrollTop = el.scrollHeight
  }, [messages])

  /*
   * A jump from the bookmarks list (App's drawer; agents/bookmarks/jump.ts): older pages are loaded back to the message,
   * then it is brought to the middle of the room and lit. One no longer in the room is said so to the caller.
   */
  const [goTo, setGoTo] = useState<{ seq: number; j: Jump } | null>(null)
  // Ready once the newest page is here: what is older is loaded back to from there.
  const canJump = bmOn && !loading && messages.length > 0
  const live = useRef({ canJump, reach })
  live.current = { canJump, reach }
  const takeIt = useCallback(() => {
    if (!live.current.canJump) return
    const j = takeJump(null, roomJumpSession(project.project_id))
    if (!j) return
    // Not followed to the tail while going back: the pages landing must not pull the room down again.
    stick.current = false
    void live.current.reach(j.index).then((ok) => (ok ? setGoTo({ seq: j.index, j }) : j.onResult?.('unreachable')))
  }, [project.project_id])
  useEffect(() => onJump(takeIt), [takeIt])
  // A jump asked for before this room could go anywhere (it was just opened): taken once it can.
  useEffect(() => {
    if (canJump) takeIt()
  }, [canJump, takeIt])
  useEffect(() => {
    if (!goTo) return
    setGoTo(null)
    const el = log.current?.querySelector<HTMLElement>(`.pmsg[data-seq="${goTo.seq}"]`)
    if (!el) {
      goTo.j.onResult?.('unreachable')
      return
    }
    stick.current = false
    el.scrollIntoView({ block: 'center' })
    el.classList.add('bm-flash')
    el.addEventListener('animationend', () => el.classList.remove('bm-flash'), { once: true })
    goTo.j.onResult?.('shown')
  }, [goTo, messages])


  return (
    <aside
      id="projPanel"
      className={dropping ? 'dropping' : undefined}
      aria-label="Project chat"
      onDragOver={(e) => {
        // preventDefault on BOTH dragover and drop, or the browser navigates to the file.
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
          setDropping(true)
        }
      }}
      onDragLeave={(e) => {
        // Fires for children too, so only clear when the pointer really left the panel.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        // The app also listens for drops at the window, so the terminal and every
        // other view accept files too. Without this the panel and the window handler
        // would both stage the same drop and every file would appear twice.
        e.stopPropagation()
        setDropping(false)
        onDropFiles([...e.dataTransfer.files])
      }}
    >
      <BusyAgents
        members={members}
        myEmail={selfEmail}
        byHash={idle.byHash}
        byKey={idle.byKey}
        convBusy={convBusy}
        onJump={onJumpAgent}
      />

      <ProjectRoster
        project={project}
        selfEmail={selfEmail}
        serverName={serverName}
        open={rosterOpen}
        onClose={onCloseRoster}
        onLeft={onLeft}
        // A group's room follows its group: people come and go by the group, never from here.
        locked={locked || !!project.group_id}
      />

      <div
        id="projMsgs"
        role="log"
        aria-live="polite"
        ref={log}
        onScroll={(e) => {
          const el = e.currentTarget
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60
          if (el.scrollTop < 300) askOlder()
        }}
      >
        {hasOlder && messages.length > 0 && (
          <div className="pm-older">
            {loadingOlder ? (
              'Loading earlier messages…'
            ) : (
              <button type="button" className="pm-older-btn" onClick={askOlder}>
                Show earlier messages
              </button>
            )}
          </div>
        )}
        {loading && messages.length === 0 && <div className="pm-empty">loading history…</div>}
        {!loading && messages.length === 0 && !error && (
          <EmptyChatGuide kind={kind} name={project.name ?? 'this chat'} />
        )}
        {/* Oldest first: useProjectMessages sorts by (ts, cursor), so the transcript
            reads top-to-bottom in historical order like the original. */}
        {(threaded ? threads.roots : messages).map((m) => {
          if (!threaded) return one(m)
          const replies = settled(m) ? (threads.repliesOf.get(m.cursor) ?? []) : []
          const open = openThreads.has(m.cursor)
          const to = replyToOf(m)
          return one(m, {
            // A reply whose message is older than what is loaded: say what it answers.
            ...(to !== null
              ? {
                  quote: (
                    <ReplyQuote
                      // Until the roster has loaded, nobody is "someone who left".
                      name={
                        typeof m['reply_to_agent_name'] === 'string' && m['reply_to_agent_name']
                          ? m['reply_to_agent_name']
                          : m['reply_to_sender'] && members.length
                            ? nameOf(String(m['reply_to_sender']))
                            : 'someone'
                      }
                      text={String(m['reply_to_text'] ?? '')}
                    />
                  ),
                }
              : {}),
            thread: (
              <>
                <ThreadBar
                  count={replies.length}
                  fresh={open ? 0 : unseen(replies)}
                  open={open}
                  onToggle={() => toggleThread(m.cursor)}
                  {...(onReply && settled(m)
                    ? { onReply: () => reply(m, to ?? m.cursor) }
                    : {})}
                />
                {open && replies.length > 0 && (
                  <div className="pm-replies" role="group" aria-label={`Replies to ${messageName(m)}`}>
                    {replies.map((r) =>
                      one(r, {
                        thread: (
                          <ThreadBar
                            count={0}
                            open={false}
                            onToggle={() => {}}
                            {...(onReply && settled(r)
                              ? { onReply: () => reply(r, m.cursor) }
                              : {})}
                          />
                        ),
                      }),
                    )}
                  </div>
                )}
              </>
            ),
          })
        })}
        {reactingTo != null && (
          <ReactionPicker
            reactions={rx.bySeq[reactingTo] ?? []}
            self={rx.self}
            onPick={(emoji) => rx.toggle(reactingTo, emoji)}
            onClose={() => setReactingTo(null)}
          />
        )}
      </div>
      {error && <div id="projErr" role="alert">{error}</div>}
      {/* A group's posting limit, where it holds the viewer (groups/GroupQuotaBar; owner, 2026-10-20). */}
      {project.group_id && (
        // My settled rows only — mine and my agents' are what the limit counts, and a send counts once the server
        // has answered it (sent or refused), not while pending. Everyone else's messages cost no re-read.
        <GroupQuotaBar
          conv={project.project_id}
          gid={project.group_id}
          bump={messages.filter((m) => m['mine'] === true && m.status !== 'pending').length}
        />
      )}

      {filesOpen && (
        <ProjectFiles
          projectId={project.project_id}
          files={files}
          onClose={onCloseFiles}
          onOpenDoc={onOpenDoc}
          onDeleted={() => void sync()}
        />
      )}
    </aside>
  )
}
